// Right-side drawer that surfaces the §9.6 prior-call composer for
// audit before dispatch. Renders the 5 layers with their mutability
// annotations and exposes an inline L4 override editor. Saving the
// override commits via the parent — which captures an audit entry
// (append-only) and persists the new summary so the next dispatch
// uses the operator's edit, not the original analyzer summary.

import React, { useEffect, useState } from 'react';

import { Button, Card, Pill } from '../../../ui';
import {
  PRIOR_CALL_LAYERS,
  type PriorCallLayer,
} from './composedPrompt';

export interface ContactPriorCallDrawerProps {
  /** Per-layer detail strings (output of buildLayerSummaries). */
  layerDetails: Record<PriorCallLayer['id'], string>;
  /** Committed L4 summary — persists between dispatches. */
  summary: string;
  saving: boolean;
  /** Persists the override; parent writes the audit entry. */
  onSaveOverride: (newSummary: string) => Promise<void> | void;
}

const ContactPriorCallDrawer: React.FC<ContactPriorCallDrawerProps> = ({
  layerDetails,
  summary,
  saving,
  onSaveOverride,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(summary);

  useEffect(() => {
    if (!editing) setDraft(summary);
  }, [summary, editing]);

  const handleSave = async () => {
    if (draft.trim() === summary.trim()) {
      setEditing(false);
      return;
    }
    await onSaveOverride(draft);
    setEditing(false);
  };

  return (
    <Card className="lg:sticky lg:top-3 self-start" padding="default">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">
          Prior-call context
        </span>
        <Pill intent="info">5 layers</Pill>
      </div>
      <p className="text-xs text-slate-400 mt-1.5 mb-3.5 leading-relaxed">
        What the agent will see on the next dispatch. Audit, override, or annotate.
      </p>

      <ul>
        {PRIOR_CALL_LAYERS.map((row, i) => (
          <li
            key={row.id}
            className={`py-2.5 ${i === 0 ? '' : 'border-t border-white/[0.08]'}`}
          >
            <div className="flex justify-between gap-2 font-mono text-[11px] text-slate-400 mb-1">
              <span>
                {row.id} · {row.label}
              </span>
              <span className="text-slate-500">{row.mutability}</span>
            </div>
            <div className="text-xs text-slate-300 leading-relaxed whitespace-pre-line">
              {layerDetails[row.id]}
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-3.5 p-2.5 rounded-ds-lg border border-dashed border-white/[0.16] bg-surface-2">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] font-semibold text-slate-200">
            L4 expanded · last call summary
          </span>
          {!editing && (
            <Button
              intent="ghost"
              size="sm"
              onClick={() => {
                setDraft(summary);
                setEditing(true);
              }}
              disabled={saving}
            >
              Override
            </Button>
          )}
        </div>
        {editing ? (
          <>
            <textarea
              className="w-full bg-surface-1 border border-white/[0.08] rounded-ds-md text-slate-50 px-2.5 py-2 text-[11px] outline-none focus:border-accent resize-y leading-relaxed"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={5}
              disabled={saving}
            />
            <div className="flex gap-1.5 mt-2">
              <Button
                intent="primary"
                size="sm"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button
                intent="ghost"
                size="sm"
                onClick={() => {
                  setDraft(summary);
                  setEditing(false);
                }}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <div className="text-[11px] text-slate-300 leading-relaxed whitespace-pre-line">
            {summary || '(no prior calls — first dispatch)'}
          </div>
        )}
      </div>
    </Card>
  );
};

export default ContactPriorCallDrawer;
